import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Building2,
  RotateCcw,
  Trash2,
  CheckCircle2,
  Layers,
  Loader2,
  Pencil,
  Plus,
  Search,
  Settings2,
  XCircle,
} from "lucide-react";

import api, { formatApiError } from "@/lib/api";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import ClientDetailsDialog from "@/components/clients/ClientDetailsDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const EMPTY_FORM = {
  name: "",
  code: "",
  legal_name: "",
  industry: "",
  website: "",
  gst_number: "",
  contact_person: "",
  contact_designation: "",
  contact_email: "",
  contact_phone: "",
  address: "",
  city: "",
  state: "",
  country: "India",
  postal_code: "",
};

function formatDate(value) {
  if (!value) return "—";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleDateString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function StatusBadge({ status }) {
  const active = status === "active";

  return (
    <span
      className={[
        "inline-flex items-center gap-1 rounded-full border px-2 py-1",
        "text-[10px] font-semibold uppercase tracking-wider",
        active
          ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300"
          : "border-slate-500/30 bg-slate-500/10 text-slate-300",
      ].join(" ")}
    >
      {active ? (
        <CheckCircle2 className="h-3 w-3" />
      ) : (
        <XCircle className="h-3 w-3" />
      )}
      {active ? "Active" : "Inactive"}
    </span>
  );
}

export default function OrganizationsAdminPage() {
  const [organizations, setOrganizations] = useState([]);
  const [applicationCounts, setApplicationCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState("");
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const [form, setForm] = useState(EMPTY_FORM);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);

  const [applicationDialog, setApplicationDialog] = useState(null);
  const [applications, setApplications] = useState([]);
  const [legacyAllAccess, setLegacyAllAccess] = useState(false);
  const [applicationsLoading, setApplicationsLoading] = useState(false);
  const [applicationUpdatingId, setApplicationUpdatingId] = useState(null);

  const loadApplicationCounts = async (items) => {
    const results = await Promise.allSettled(
      items.map(async (organization) => {
        const response = await api.get(
          `/organizations/${organization.id}/applications`
        );

        const assignedCount = (response.data?.applications || []).filter(
          (application) => application.assigned
        ).length;

        return {
          organizationId: organization.id,
          assignedCount,
        };
      })
    );

    const counts = {};

    results.forEach((result) => {
      if (result.status === "fulfilled") {
        counts[result.value.organizationId] = result.value.assignedCount;
      }
    });

    setApplicationCounts(counts);
  };

  const loadOrganizations = async () => {
    setLoading(true);

    try {
      const response = await api.get("/organizations", {
        params: { include_deleted: includeDeleted },
      });
      const items = Array.isArray(response.data) ? response.data : [];

      setOrganizations(items);
      await loadApplicationCounts(items);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrganizations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeDeleted]);

  const filteredOrganizations = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();

    if (!normalizedQuery) return organizations;

    return organizations.filter((organization) => {
      return (
        String(organization.name || "")
          .toLowerCase()
          .includes(normalizedQuery) ||
        String(organization.code || "")
          .toLowerCase()
          .includes(normalizedQuery) ||
        String(organization.status || "")
          .toLowerCase()
          .includes(normalizedQuery)
      );
    });
  }, [organizations, query]);

  // Show Deleted switches the grid to deleted clients only, never a mix.
  const visibleClients = filteredOrganizations.filter(
    (organization) => Boolean(organization.deleted) === includeDeleted
  );

  const createOrganization = async () => {
    const name = form.name.trim();
    const code = form.code.trim().toUpperCase();

    if (!name) {
      toast.error("Client name is required");
      return;
    }

    if (!code) {
      toast.error("Client code is required");
      return;
    }

    if (!form.contact_person.trim()) {
      toast.error("Contact person name is required");
      return;
    }

    if (!form.contact_email.trim()) {
      toast.error("Contact email is required");
      return;
    }

    if (!form.contact_phone.trim()) {
      toast.error("Contact phone is required");
      return;
    }

    setSaving(true);

    try {
      await api.post("/organizations", {
        ...form,
        name,
        code,
        primary_contact: {
          name: form.contact_person.trim(),
          designation: form.contact_designation.trim(),
          email: form.contact_email.trim(),
          phone: form.contact_phone.trim(),
        },
      });

      toast.success("Client created successfully");
      setForm(EMPTY_FORM);
      setCreateDialogOpen(false);
      await loadOrganizations();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSaving(false);
    }
  };

  const openEditDialog = (organization) => {
    const primaryContact = organization.primary_contact || {};

    setEditing({
      id: organization.id,
      name: organization.name || "",
      code: organization.code || "",
      status: organization.status || "active",
      legal_name: organization.legal_name || "",
      industry: organization.industry || "",
      website: organization.website || "",
      gst_number: organization.gst_number || "",
      contact_person:
        primaryContact.name || organization.contact_person || "",
      contact_designation:
        primaryContact.designation ||
        organization.contact_designation ||
        "",
      contact_email:
        primaryContact.email || organization.contact_email || "",
      contact_phone:
        primaryContact.phone || organization.contact_phone || "",
      address: organization.address || "",
      city: organization.city || "",
      state: organization.state || "",
      country: organization.country || "India",
      postal_code: organization.postal_code || "",
    });
  };

  const updateOrganization = async () => {
    if (!editing) return;

    const name = editing.name.trim();
    const code = editing.code.trim().toUpperCase();

    if (!name) {
      toast.error("Client name is required");
      return;
    }

    if (!code) {
      toast.error("Client code is required");
      return;
    }

    setSaving(true);

    try {
      await api.patch(`/organizations/${editing.id}`, {
        ...editing,
        name,
        code,
        status: editing.status,
        primary_contact: {
          name: editing.contact_person?.trim() || "",
          designation: editing.contact_designation?.trim() || "",
          email: editing.contact_email?.trim() || "",
          phone: editing.contact_phone?.trim() || "",
        },
      });

      toast.success("Client updated successfully");
      setEditing(null);
      await loadOrganizations();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async (id) => {
    try {
      await api.delete(`/organizations/${id}`);
      toast.success("Client deleted successfully");
      setConfirmDelete(null);
      await loadOrganizations();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const doRestore = async (id) => {
    try {
      await api.post(`/organizations/${id}/restore`);
      toast.success("Client restored successfully");
      await loadOrganizations();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const loadOrganizationApplications = async (organization) => {
    setApplicationDialog(organization);
    setApplications([]);
    setApplicationsLoading(true);

    try {
      const response = await api.get(
        `/organizations/${organization.id}/applications`
      );

      setApplications(response.data?.applications || []);
      setLegacyAllAccess(Boolean(response.data?.legacy_all_access));
    } catch (error) {
      toast.error(formatApiError(error));
      setApplicationDialog(null);
    } finally {
      setApplicationsLoading(false);
    }
  };

  const refreshOrganizationApplications = async () => {
    if (!applicationDialog) return;

    const response = await api.get(
      `/organizations/${applicationDialog.id}/applications`
    );

    const items = response.data?.applications || [];

    setApplications(items);
    setLegacyAllAccess(Boolean(response.data?.legacy_all_access));

    const assignedCount = items.filter(
      (application) => application.assigned
    ).length;

    setApplicationCounts((current) => ({
      ...current,
      [applicationDialog.id]: assignedCount,
    }));
  };

  const toggleApplication = async (application, enabled) => {
    if (!applicationDialog || applicationUpdatingId) return;

    setApplicationUpdatingId(application.id);

    setApplications((current) =>
      current.map((item) =>
        item.id === application.id
          ? {
              ...item,
              assigned: enabled,
            }
          : item
      )
    );

    try {
      if (enabled) {
        await api.post(
          `/organizations/${applicationDialog.id}/applications/${application.id}`
        );
        toast.success(`${application.name} assigned`);
      } else {
        await api.delete(
          `/organizations/${applicationDialog.id}/applications/${application.id}`
        );
        toast.success(`${application.name} removed`);
      }

      await refreshOrganizationApplications();
    } catch (error) {
      toast.error(formatApiError(error));
      await refreshOrganizationApplications().catch(() => {});
    } finally {
      setApplicationUpdatingId(null);
    }
  };

  const currentOrganizations = organizations.filter(
    (organization) => !organization.deleted
  );
  const activeOrganizations = currentOrganizations.filter(
    (organization) => organization.status === "active"
  ).length;

  return (
    <TooltipProvider delayDuration={200}>
      <div
        className="space-y-4"
        data-testid="admin-organizations-page"
      >
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
              Administration
            </div>

            <h1 className="font-display text-3xl font-extrabold tracking-tight">
              Clients
            </h1>

            <p className="mt-1 max-w-2xl text-xs text-muted-foreground">
              Manage client organizations, application access and service relationships.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div className="rounded-lg border border-border bg-card/70 px-3 py-2">
              <div className="text-[9px] uppercase tracking-widest text-muted-foreground">
                Total
              </div>
              <div className="text-lg font-bold">
                {currentOrganizations.length}
              </div>
            </div>

            <div className="rounded-lg border border-border bg-card/70 px-3 py-2">
              <div className="text-[9px] uppercase tracking-widest text-muted-foreground">
                Active
              </div>
              <div className="text-lg font-bold text-emerald-300">
                {activeOrganizations}
              </div>
            </div>

            <Button
              onClick={() => setCreateDialogOpen(true)}
              className="h-10 bg-[#d4af37] px-4 text-black hover:bg-[#e5c158]"
              data-testid="organization-add-client"
            >
              <Plus className="mr-2 h-4 w-4" />
              Add Client
            </Button>
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-card/60 p-3">
            <div className="relative min-w-[260px] flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />

              <Input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search client name, code or status…"
                className="pl-9"
                data-testid="organizations-search"
              />
            </div>

            <div className="whitespace-nowrap text-xs text-muted-foreground">
              {visibleClients.length} {includeDeleted ? "deleted " : ""}client
              {visibleClients.length === 1 ? "" : "s"}
            </div>

            <div className="flex items-center gap-2 text-xs">
              <span className="text-muted-foreground">
                Show Deleted
              </span>

              <Switch
                data-testid="organizations-show-deleted"
                checked={includeDeleted}
                onCheckedChange={setIncludeDeleted}
              />
            </div>
          </div>

          <div className="overflow-hidden rounded-xl border border-border bg-card/70">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[900px] text-sm">
                <thead className="bg-secondary/20 text-[11px] uppercase tracking-widest text-muted-foreground">
                  <tr className="border-b border-border">
                    <th className="w-[34%] px-4 py-3 text-left">
                      Client
                    </th>
                    <th className="w-[14%] px-4 py-3 text-left">
                      Code
                    </th>
                    <th className="w-[18%] px-4 py-3 text-left">
                      Applications
                    </th>
                    <th className="w-[12%] px-4 py-3 text-left">
                      Status
                    </th>
                    <th className="w-[14%] px-4 py-3 text-left">
                      Created
                    </th>
                    <th className="w-[8%] px-4 py-3 text-right">
                      Actions
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {loading && (
                    <tr>
                      <td
                        colSpan={6}
                        className="px-4 py-12 text-center text-xs text-muted-foreground"
                      >
                        <Loader2 className="mx-auto mb-2 h-5 w-5 animate-spin" />
                        Loading clients…
                      </td>
                    </tr>
                  )}

                  {!loading &&
                    visibleClients.map((organization) => (
                      <tr
                        key={organization.id}
                        data-row
                        onClick={() => !organization.deleted && setSelectedClient(organization)}
                        className={`border-b border-border/70 transition-colors last:border-b-0 ${
                          organization.deleted
                            ? "opacity-70"
                            : "cursor-pointer hover:bg-white/[0.04]"
                        }`}
                        data-testid={`organization-row-${organization.id}`}
                      >
                        <td className="px-4 py-3.5">
                          <div className="flex min-w-0 items-center gap-3">
                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-[#d4af37]/25 bg-[#d4af37]/10 text-[#e5c158]">
                              <Building2 className="h-4 w-4" />
                            </div>

                            <div className="min-w-0">
                              <div className="truncate font-semibold text-foreground">
                                {organization.name}
                              </div>

                              <div className="mt-0.5 max-w-[360px] truncate text-[11px] text-muted-foreground">
                                ID: {organization.id}
                              </div>
                            </div>
                          </div>
                        </td>

                        <td className="px-4 py-3.5">
                          <span className="inline-flex max-w-full rounded-md border border-border bg-background/50 px-2 py-1 font-mono text-xs">
                            {organization.code}
                          </span>
                        </td>

                        <td className="px-4 py-3.5">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                type="button"
                                disabled={organization.deleted}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  loadOrganizationApplications(organization);
                                }}
                                className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-border bg-background/30 px-2.5 py-1.5 text-xs transition-colors hover:border-[#d4af37]/40 hover:bg-[#d4af37]/5"
                              >
                                <Layers className="h-3.5 w-3.5 text-[#e5c158]" />
                                {applicationCounts[organization.id] ?? "—"} assigned
                              </button>
                            </TooltipTrigger>

                            <TooltipContent>
                              View and manage assigned applications
                            </TooltipContent>
                          </Tooltip>
                        </td>

                        <td className="px-4 py-3.5">
                          {organization.deleted ? (
                            <span className="inline-flex items-center gap-1 rounded-full border border-rose-500/30 bg-rose-500/10 px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-rose-300">
                              <XCircle className="h-3 w-3" />
                              Deleted
                            </span>
                          ) : (
                            <StatusBadge status={organization.status} />
                          )}
                        </td>

                        <td className="px-4 py-3.5 text-xs text-muted-foreground">
                          {formatDate(organization.created_at)}
                        </td>

                        <td className="px-4 py-3.5 text-right">
                          {organization.deleted ? (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="outline"
                                  size="icon"
                                  onClick={() => doRestore(organization.id)}
                                  data-testid={`organization-restore-${organization.code}`}
                                  className="h-8 w-8"
                                  aria-label={`Restore ${organization.name}`}
                                >
                                  <RotateCcw className="h-3.5 w-3.5" />
                                </Button>
                              </TooltipTrigger>

                              <TooltipContent>
                                Restore Client
                              </TooltipContent>
                            </Tooltip>
                          ) : (
                            <div
                              className="inline-flex items-center gap-1.5"
                              onClick={(event) => event.stopPropagation()}
                            >
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="outline"
                                    size="icon"
                                    onClick={() => openEditDialog(organization)}
                                    data-testid={`organization-edit-${organization.code}`}
                                    className="h-8 w-8"
                                    aria-label={`Edit ${organization.name}`}
                                  >
                                    <Pencil className="h-3.5 w-3.5" />
                                  </Button>
                                </TooltipTrigger>

                                <TooltipContent>
                                  Edit Client
                                </TooltipContent>
                              </Tooltip>

                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="outline"
                                    size="icon"
                                    onClick={() => setConfirmDelete(organization)}
                                    data-testid={`organization-delete-${organization.code}`}
                                    className="h-8 w-8 border-rose-500/30 text-rose-400 hover:bg-rose-500/10"
                                    aria-label={`Delete ${organization.name}`}
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </Button>
                                </TooltipTrigger>

                                <TooltipContent>
                                  Delete Client
                                </TooltipContent>
                              </Tooltip>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}

                  {!loading && visibleClients.length === 0 && (
                    <tr>
                      <td
                        colSpan={6}
                        className="px-4 py-14 text-center"
                      >
                        <Building2 className="mx-auto mb-3 h-8 w-8 text-muted-foreground/50" />

                        <div className="text-sm font-semibold">
                          {includeDeleted ? "No deleted clients found" : "No clients found"}
                        </div>

                        <div className="mt-1 text-xs text-muted-foreground">
                          {query
                            ? "Try changing your search."
                            : includeDeleted
                              ? "Deleted clients will appear here."
                              : "Create your first client to get started."}
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <Dialog
          open={createDialogOpen}
          onOpenChange={(open) => {
            if (!saving) {
              setCreateDialogOpen(open);

              if (!open) {
                setForm(EMPTY_FORM);
              }
            }
          }}
        >
          <DialogContent className="max-w-xl" data-testid="create-organization-dialog">
            <DialogHeader>
              <DialogTitle>Add client</DialogTitle>
              <DialogDescription>
                Add the client identity and primary point of contact.
              </DialogDescription>
            </DialogHeader>

            <div className="max-h-[65vh] space-y-4 overflow-y-auto pr-1">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="organization-name">
                    Client name *
                  </Label>

                  <Input
                    id="organization-name"
                    value={form.name}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        name: event.target.value,
                      }))
                    }
                    placeholder="Example: ABC Industries"
                    data-testid="organization-name"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="organization-code">
                    Client code *
                  </Label>

                  <Input
                    id="organization-code"
                    value={form.code}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        code: event.target.value.toUpperCase(),
                      }))
                    }
                    placeholder="Example: ABC"
                    maxLength={30}
                    data-testid="organization-code"
                  />
                </div>
              </div>

              <div className="rounded-lg border border-border bg-secondary/10 px-3 py-2 text-[11px] text-muted-foreground">
                Client code must be unique and will automatically be saved in uppercase.
              </div>

              <div className="border-t border-border pt-4">
                <div className="mb-3 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                  Primary point of contact
                </div>

                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label>Contact person *</Label>

                    <Input
                      value={form.contact_person}
                      onChange={(event) =>
                        setForm((current) => ({
                          ...current,
                          contact_person: event.target.value,
                        }))
                      }
                      placeholder="Contact person name"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label>Designation</Label>

                    <Input
                      value={form.contact_designation}
                      onChange={(event) =>
                        setForm((current) => ({
                          ...current,
                          contact_designation: event.target.value,
                        }))
                      }
                      placeholder="Operations Manager"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label>Email *</Label>

                    <Input
                      type="email"
                      value={form.contact_email}
                      onChange={(event) =>
                        setForm((current) => ({
                          ...current,
                          contact_email: event.target.value,
                        }))
                      }
                      placeholder="contact@client.com"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label>Phone *</Label>

                    <Input
                      value={form.contact_phone}
                      onChange={(event) =>
                        setForm((current) => ({
                          ...current,
                          contact_phone: event.target.value,
                        }))
                      }
                      placeholder="+91..."
                    />
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                disabled={saving}
                onClick={() => {
                  setCreateDialogOpen(false);
                  setForm(EMPTY_FORM);
                }}
              >
                Cancel
              </Button>

              <Button
                onClick={createOrganization}
                disabled={saving}
                className="bg-[#d4af37] text-black hover:bg-[#e5c158]"
                data-testid="organization-create"
              >
                {saving ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Plus className="mr-2 h-4 w-4" />
                )}

                Create client
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

      <ClientDetailsDialog
        client={selectedClient}
        open={Boolean(selectedClient)}
        onOpenChange={(open) => {
          if (!open) setSelectedClient(null);
        }}
      />

      <Dialog
        open={Boolean(editing)}
        onOpenChange={(open) => {
          if (!open && !saving) setEditing(null);
        }}
      >
        <DialogContent data-testid="edit-organization-dialog">
          <DialogHeader>
            <DialogTitle>Edit client</DialogTitle>
            <DialogDescription>
              Update the client identity or temporarily deactivate its
              account.
            </DialogDescription>
          </DialogHeader>

          {editing && (
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label>Name</Label>

                <Input
                  value={editing.name}
                  onChange={(event) =>
                    setEditing((current) => ({
                      ...current,
                      name: event.target.value,
                    }))
                  }
                  data-testid="edit-organization-name"
                />
              </div>

              <div className="space-y-1.5">
                <Label>Code</Label>

                <Input
                  value={editing.code}
                  onChange={(event) =>
                    setEditing((current) => ({
                      ...current,
                      code: event.target.value.toUpperCase(),
                    }))
                  }
                  data-testid="edit-organization-code"
                />
              </div>

              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div className="space-y-1.5">
                  <Label>Contact person</Label>
                  <Input
                    value={editing.contact_person || ""}
                    onChange={(event) =>
                      setEditing((current) => ({
                        ...current,
                        contact_person: event.target.value,
                      }))
                    }
                  />
                </div>

                <div className="space-y-1.5">
                  <Label>Designation</Label>
                  <Input
                    value={editing.contact_designation || ""}
                    onChange={(event) =>
                      setEditing((current) => ({
                        ...current,
                        contact_designation: event.target.value,
                      }))
                    }
                  />
                </div>

                <div className="space-y-1.5">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={editing.contact_email || ""}
                    onChange={(event) =>
                      setEditing((current) => ({
                        ...current,
                        contact_email: event.target.value,
                      }))
                    }
                  />
                </div>

                <div className="space-y-1.5">
                  <Label>Phone</Label>
                  <Input
                    value={editing.contact_phone || ""}
                    onChange={(event) =>
                      setEditing((current) => ({
                        ...current,
                        contact_phone: event.target.value,
                      }))
                    }
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label>Status</Label>

                <Select
                  value={editing.status}
                  onValueChange={(value) =>
                    setEditing((current) => ({
                      ...current,
                      status: value,
                    }))
                  }
                >
                  <SelectTrigger data-testid="edit-organization-status">
                    <SelectValue />
                  </SelectTrigger>

                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditing(null)}
              disabled={saving}
            >
              Cancel
            </Button>

            <Button
              onClick={updateOrganization}
              disabled={saving}
              className="bg-[#d4af37] text-black hover:bg-[#e5c158]"
              data-testid="edit-organization-save"
            >
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(applicationDialog)}
        onOpenChange={(open) => {
          if (!open && !applicationUpdatingId) {
            setApplicationDialog(null);
            setApplications([]);
            setLegacyAllAccess(false);
          }
        }}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Layers className="h-5 w-5 text-[#e5c158]" />
              Manage applications
            </DialogTitle>

            <DialogDescription>
              {applicationDialog
                ? `Choose which applications ${applicationDialog.name} can access.`
                : "Choose the applications this client can access."}
            </DialogDescription>
          </DialogHeader>

          {legacyAllAccess && !applicationsLoading && (
            <div className="rounded-lg border border-amber-500/25 bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
              This client currently uses legacy all-application access.
              Changing any switch will convert it to explicit application
              assignments while preserving the other current assignments.
            </div>
          )}

          <div className="max-h-[55vh] space-y-2 overflow-y-auto pr-1">
            {applicationsLoading && (
              <div className="py-12 text-center text-xs text-muted-foreground">
                <Loader2 className="mx-auto mb-2 h-5 w-5 animate-spin" />
                Loading applications…
              </div>
            )}

            {!applicationsLoading &&
              applications.map((application) => (
                <div
                  key={application.id}
                  className="flex items-center justify-between gap-4 rounded-xl border border-border bg-background/30 p-3"
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border bg-card">
                      <Layers className="h-4 w-4 text-[#e5c158]" />
                    </div>

                    <div className="min-w-0">
                      <div className="truncate text-sm font-semibold">
                        {application.name}
                      </div>

                      <div className="flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
                        <span>{application.code}</span>

                        {application.description && (
                          <>
                            <span>•</span>
                            <span className="max-w-[360px] truncate">
                              {application.description}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex shrink-0 items-center gap-2">
                    {applicationUpdatingId === application.id && (
                      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                    )}

                    <Switch
                      checked={Boolean(application.assigned)}
                      disabled={Boolean(applicationUpdatingId)}
                      onCheckedChange={(checked) =>
                        toggleApplication(application, checked)
                      }
                      data-testid={`organization-application-${application.id}`}
                    />
                  </div>
                </div>
              ))}

            {!applicationsLoading && applications.length === 0 && (
              <div className="py-12 text-center text-xs text-muted-foreground">
                No applications are available.
              </div>
            )}
          </div>

          <DialogFooter>
            <div className="mr-auto text-xs text-muted-foreground">
              {
                applications.filter((application) => application.assigned)
                  .length
              }{" "}
              of {applications.length} applications assigned
            </div>

            <Button
              variant="outline"
              disabled={Boolean(applicationUpdatingId)}
              onClick={() => setApplicationDialog(null)}
            >
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ConfirmDialog
        open={!!confirmDelete}
        onOpenChange={(open) =>
          !open && setConfirmDelete(null)
        }
        title={
          confirmDelete
            ? `Delete "${confirmDelete.name}"?`
            : "Delete?"
        }
        description="Soft delete — the client will be hidden but preserved. You can restore it later from Show Deleted."
        confirmLabel="Delete"
        destructive
        onConfirm={() =>
          confirmDelete &&
          doDelete(confirmDelete.id)
        }
        testid="confirm-delete-organization"
      />

      </div>
    </TooltipProvider>
  );
}
